import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/services/api/client";
import {
  PermissionContextValue,
  PermissionState,
  PermissionCheck,
  PermissionResult,
  BulkPermissionCheck,
  BulkPermissionResult,
  Permission,
  SystemRole,
  ProjectRole,
  ComponentRole,
  RoleDefinition,
  RoleAssignment,
  ComponentPermissionSet,
  Organization,
  Department,
  Team,
  OrganizationMembership,
  TemporalPermission,
  ResourceType,
  PermissionAction,
  ComponentPermissionType,
  PermissionRule,
} from "@/types/permissions";
import { config } from "@/config";
import * as errorUtils from '@/utils/errors';
import {
  getViewerPermissionIdString,
  getViewerPermissionIdStrings,
} from "@/components/permissions/viewerPermissionIds";

const PermissionContext = createContext<PermissionContextValue | undefined>(
  undefined,
);

const requiresExplicitOrganizationContext = (resource: {
  type: ResourceType;
  organizationId?: string;
}): boolean =>
  !resource.organizationId && resource.type !== ResourceType.PROJECT;

const createUnauthenticatedPermissionResult = (): PermissionResult => ({
  allowed: false,
  reason: "Permission denied: user not authenticated",
  evaluatedAt: new Date(),
});

// API service for permissions
const permissionApi = {
  fetchUserPermissions: async (userId: string): Promise<PermissionState> => {
    // Use apiClient for proper error handling, CSRF token attachment, and 429 retry logic
    const response = await apiClient.get(`/permissions/user/${userId}`);
    // apiClient returns AxiosResponse, data contains the actual response
    if (!response.data?.success && !response.data?.data) {
      // Check for both wrapped and unwrapped response formats
      if (response.data) {
        return response.data;
      }
      throw new Error("Failed to fetch permissions");
    }
    return response.data.data || response.data;
  },

  checkPermission: async (
    check: PermissionCheck,
  ): Promise<PermissionResult> => {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error("User not authenticated");
    }
    if (requiresExplicitOrganizationContext(check.resource)) {
      throw new Error("Organization context required for permission check");
    }

    // Ensure the request matches backend schema expectations
    const requestPayload = {
      resource: {
        id: check.resource.id,
        type: check.resource.type,
        ownerId: check.resource.ownerId,
        organizationId: check.resource.organizationId,
        teamId: check.resource.teamId,
        projectId: check.resource.projectId,
        attributes: check.resource.attributes || {},
      },
      action: check.action,
      context: {
        environment: {
          timestamp: new Date().toISOString(),
          ipAddress: "unknown",
          deviceType: "desktop",
          networkType: "trusted",
          trustScore: 1.0,
        },
      },
    };

    try {
      // Use apiClient for proper error handling and 429 retry logic
      const response = await apiClient.post(`/permissions/check`, requestPayload);
      return response.data;
    } catch (error: unknown) {
      const status = errorUtils.getErrorStatus(error);
      console.error("❌ Permission check failed:", {
        status,
        error: errorUtils.getErrorMessage(error),
      });

      // Fail closed when the permission service is unavailable.
      if (status === 404 || status === 503) {
        const userIds = getViewerPermissionIdStrings(user.id, user._id);
        const ownerId = getViewerPermissionIdString(
          requestPayload.resource.ownerId,
        );
        const isOwner = !!(ownerId && userIds.has(ownerId));
        if (isOwner && ["READ", "UPDATE"].includes(requestPayload.action)) {
          console.warn(
            "⚠️  Permission service unavailable, denying owner access fallback",
          );
          return {
            allowed: false,
            reason:
              "Permission denied: service unavailable; owner access fallback failed closed",
            evaluatedAt: new Date(),
          };
        }
      }

      throw error;
    }
  },

  checkBulkPermissions: async (
    checks: BulkPermissionCheck,
  ): Promise<BulkPermissionResult> => {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error("User not authenticated");
    }
    const missingOrgCheck = checks.checks.find((check) =>
      requiresExplicitOrganizationContext(check.resource),
    );
    if (missingOrgCheck) {
      throw new Error("Organization context required for bulk permission check");
    }

    // Transform checks to match backend schema
    const requestPayload = {
      checks: checks.checks.map((check) => ({
        id: `${check.resource.type}-${check.resource.id}-${check.action}`,
        resource: {
          id: check.resource.id,
          type: check.resource.type,
          ownerId: check.resource.ownerId,
          organizationId: check.resource.organizationId,
          teamId: check.resource.teamId,
          projectId: check.resource.projectId,
          attributes: check.resource.attributes || {},
        },
        action: check.action,
        context: {
          environment: {
            timestamp: new Date().toISOString(),
            ipAddress: "unknown",
            deviceType: "desktop",
            networkType: "trusted",
            trustScore: 1.0,
          },
        },
      })),
    };

    try {
      // Use apiClient for proper error handling and 429 retry logic
      const response = await apiClient.post(`/permissions/check-bulk`, requestPayload);
      return response.data;
    } catch (error: unknown) {
      console.error("❌ Bulk permission check failed:", {
        status: errorUtils.getErrorStatus(error),
        error: errorUtils.getErrorMessage(error),
      });
      throw error;
    }
  },

  assignRole: async (assignment: RoleAssignment): Promise<void> => {
    // Use apiClient for proper error handling and 429 retry logic
    await apiClient.post(`/roles/assign`, assignment);
  },

  revokeRole: async (roleId: string, userId: string): Promise<void> => {
    // Use apiClient for proper error handling and 429 retry logic
    await apiClient.delete(`/roles/revoke`, {
      data: { roleId, userId },
    });
  },

  updateComponentPermissions: async (
    componentId: string,
    permissions: Partial<ComponentPermissionSet>,
  ): Promise<void> => {
    // Use apiClient for proper error handling and 429 retry logic
    await apiClient.patch(`/components/${componentId}/permissions`, permissions);
  },
};

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated, tokens } = useAuthStore();
  const queryClient = useQueryClient();
  const permissionCacheRef = useRef<Map<string, PermissionResult>>(new Map());

  // Fetch user permissions
  const {
    data: permissionState,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["permissions", user?.id],
    queryFn: () => {
      if (!user?.id) {
        throw new Error("Cannot fetch permissions without an authenticated user");
      }

      return permissionApi.fetchUserPermissions(user.id);
    },
    enabled: !!user?.id && isAuthenticated && !!tokens?.accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache time (renamed in React Query v4+),
    retry: (failureCount, error: unknown) => {
      // Don't retry on auth errors
      if (
        errorUtils.hasErrorCode(error, "AUTHENTICATION_ERROR") ||
        errorUtils.hasErrorCode(error, "UNAUTHORIZED")
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Mutations
  const assignRoleMutation = useMutation({
    mutationFn: permissionApi.assignRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
  });

  const revokeRoleMutation = useMutation({
    mutationFn: ({ roleId, userId }: { roleId: string; userId: string }) =>
      permissionApi.revokeRole(roleId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
  });

  const updateComponentPermissionsMutation = useMutation({
    mutationFn: ({
      componentId,
      permissions,
    }: {
      componentId: string;
      permissions: Partial<ComponentPermissionSet>;
    }) => permissionApi.updateComponentPermissions(componentId, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
  });

  // Check single permission with caching and deduplication
  const checkPermission = useCallback(
    async (check: PermissionCheck): Promise<PermissionResult> => {
      if (!user?.id || !isAuthenticated || !tokens?.accessToken) {
        return createUnauthenticatedPermissionResult();
      }

      const cacheUserId = user.id;

      const cacheKey = `${check.resource.type}:${check.resource.id}:${check.action}:${cacheUserId}`;

      // Check cache first
      const cached = permissionCacheRef.current.get(cacheKey);
      if (cached && cached.ttl && cached.ttl > Date.now()) {
        return cached;
      }

      // Use deduplication layer for server check
      const result = await checkPermissionWithDeduplication(check, cacheUserId);

      // Update cache with longer TTL (default 30 seconds if not provided)
      const ttl = result.ttl || 30000;
      permissionCacheRef.current.set(cacheKey, {
        ...result,
        ttl: Date.now() + ttl,
      });

      return result;
    },
    [user?.id, isAuthenticated, tokens?.accessToken],
  );

  // Check bulk permissions
  const checkBulkPermissions = useCallback(
    async (checks: BulkPermissionCheck): Promise<BulkPermissionResult> => {
      const authenticatedUserId = user?.id;
      if (!authenticatedUserId || !isAuthenticated || !tokens?.accessToken) {
        return {
          results: Object.fromEntries(
            checks.checks.map((check) => [
              `${check.resource.type}-${check.resource.id}-${check.action}`,
              createUnauthenticatedPermissionResult(),
            ]),
          ),
          errors: {},
          metadata: {
            totalChecks: checks.checks.length,
            successCount: checks.checks.length,
            errorCount: 0,
            processingTime: 0,
          },
        };
      }

      // Add userId to all checks if not present
      const checksWithUser = {
        ...checks,
        checks: checks.checks.map((check) => ({
          ...check,
          userId: check.userId || authenticatedUserId,
        })),
      };

      return permissionApi.checkBulkPermissions(checksWithUser);
    },
    [user?.id, isAuthenticated, tokens?.accessToken],
  );

  // Refresh permissions
  const refreshPermissions = useCallback(async () => {
    permissionCacheRef.current = new Map(); // Clear cache
    await refetch();
  }, [refetch]);

  // Get component permissions
  // Accepts optional project context to check project ownership and membership
  const getComponentPermissions = useCallback(
    (
      componentId: string,
      projectContext?: {
        ownerId?: string | { toString(): string };
        owner_id?: string | { toString(): string };
        members?: Array<{ userId: string | { toString(): string }; role: string }>;
      },
    ): ComponentPermissionSet | undefined => {
      if (!permissionState) return undefined;

      // This would typically come from the permission state
      // For now, return a default set based on user role and project context
      const defaultRule: PermissionRule = {
        allowed: true,
        allowedRoles: [
          SystemRole.DESIGNER,
          SystemRole.ORGANIZATION_ADMIN,
          SystemRole.SUPER_ADMIN,
        ],
        allowedUsers: [],
        deniedUsers: [],
        conditions: [],
        inherited: false,
      };

      const viewerRule: PermissionRule = {
        allowed: true,
        allowedRoles: Object.values(SystemRole),
        allowedUsers: [],
        deniedUsers: [],
        conditions: [],
        inherited: false,
      };

      const restrictedRule: PermissionRule = {
        allowed: false,
        allowedRoles: [],
        allowedUsers: [],
        deniedUsers: [],
        conditions: [],
        inherited: false,
      };

      const currentUserIds = getViewerPermissionIdStrings(user?.id, user?._id);
      const ownerIdStr =
        getViewerPermissionIdString(projectContext?.ownerId) ??
        getViewerPermissionIdString(projectContext?.owner_id);
      const isProjectOwner = !!(
        ownerIdStr && currentUserIds.has(ownerIdStr)
      );

      // Check if user is a project collaborator with edit rights
      const userMembership = projectContext?.members?.find((m) => {
        const memberUserIdStr = getViewerPermissionIdString(m.userId);
        return !!(memberUserIdStr && currentUserIds.has(memberUserIdStr));
      });
      const hasEditMembership =
        userMembership &&
        ["owner", "admin", "editor"].includes(userMembership.role);

      // Check user's system role
      const isAdmin =
        permissionState.systemRole === SystemRole.SUPER_ADMIN ||
        permissionState.systemRole === SystemRole.ORGANIZATION_ADMIN;
      const isDesigner = permissionState.systemRole === SystemRole.DESIGNER;

      // User can edit if they have system-level access OR project-level access
      const canEdit = isAdmin || isDesigner || isProjectOwner || hasEditMembership;

      // Project owners and admins can delete
      const canDelete = isAdmin || isProjectOwner;

      return {
        [ComponentPermissionType.CREATE]: canEdit
          ? defaultRule
          : restrictedRule,
        [ComponentPermissionType.READ]: viewerRule,
        [ComponentPermissionType.UPDATE]: canEdit
          ? defaultRule
          : restrictedRule,
        [ComponentPermissionType.DELETE]: canDelete
          ? defaultRule
          : restrictedRule,
        [ComponentPermissionType.CLONE]: canEdit ? defaultRule : restrictedRule,
        [ComponentPermissionType.MOVE]: canEdit ? defaultRule : restrictedRule,
        [ComponentPermissionType.RESIZE]: canEdit
          ? defaultRule
          : restrictedRule,
        [ComponentPermissionType.STYLE]: canEdit ? defaultRule : restrictedRule,
        [ComponentPermissionType.EDIT_CONTENT]: canEdit
          ? defaultRule
          : restrictedRule,
        [ComponentPermissionType.EDIT_LAYOUT]: canEdit
          ? defaultRule
          : restrictedRule,
        [ComponentPermissionType.EDIT_BEHAVIOR]: canEdit
          ? defaultRule
          : restrictedRule,
        [ComponentPermissionType.COMMENT]: viewerRule,
        [ComponentPermissionType.ANNOTATE]: viewerRule,
        [ComponentPermissionType.SHARE]: canEdit ? defaultRule : restrictedRule,
        [ComponentPermissionType.EXPORT]: viewerRule,
        [ComponentPermissionType.PUBLISH]: canDelete
          ? defaultRule
          : restrictedRule,
        [ComponentPermissionType.VERSION]: canEdit
          ? defaultRule
          : restrictedRule,
      };
    },
    [permissionState, user?.id, user?._id],
  );

  // Update component permissions
  const updateComponentPermissions = useCallback(
    async (
      componentId: string,
      permissions: Partial<ComponentPermissionSet>,
    ) => {
      await updateComponentPermissionsMutation.mutateAsync({
        componentId,
        permissions,
      });
    },
    [updateComponentPermissionsMutation],
  );

  // Organization management (placeholder implementations)
  const setOrganization = useCallback(
    async (organizationId: string) => {
      // Implementation would switch organization context
      await refreshPermissions();
    },
    [refreshPermissions],
  );

  const joinTeam = useCallback(
    async (teamId: string) => {
      // Implementation would add user to team
      await refreshPermissions();
    },
    [refreshPermissions],
  );

  const leaveTeam = useCallback(
    async (teamId: string) => {
      // Implementation would remove user from team
      await refreshPermissions();
    },
    [refreshPermissions],
  );

  // Create context value
  const contextValue = useMemo<PermissionContextValue>(() => {
    const defaultState: PermissionState = {
      userPermissions: [],
      effectivePermissions: [],
      systemRole: SystemRole.VIEWER,
      projectRoles: new Map(),
      componentRoles: new Map(),
      customRoles: [],
      organization: undefined,
      departments: [],
      teams: [],
      memberships: [],
      temporalPermissions: [],
      lastFetched: new Date(),
      isLoading: isLoading,
      error: error ? errorUtils.getErrorMessage(error) : undefined,
    };

    const currentState = permissionState || defaultState;

    return {
      userPermissions: currentState.userPermissions,
      effectivePermissions: currentState.effectivePermissions,
      systemRole: currentState.systemRole,
      projectRoles: currentState.projectRoles,
      componentRoles: currentState.componentRoles,
      customRoles: currentState.customRoles,
      organization: currentState.organization,
      departments: currentState.departments,
      teams: currentState.teams,
      memberships: currentState.memberships,
      temporalPermissions: currentState.temporalPermissions,
      lastFetched: currentState.lastFetched,
      isLoading: currentState.isLoading,
      error: currentState.error,
      checkPermission,
      checkBulkPermissions,
      refreshPermissions,
      assignRole: (assignment: RoleAssignment) =>
        assignRoleMutation.mutateAsync(assignment),
      revokeRole: (roleId: string, userId: string) =>
        revokeRoleMutation.mutateAsync({ roleId, userId }),
      getComponentPermissions,
      updateComponentPermissions,
      setOrganization,
      joinTeam,
      leaveTeam,
    };
  }, [
    permissionState,
    isLoading,
    error,
    checkPermission,
    checkBulkPermissions,
    refreshPermissions,
    assignRoleMutation,
    revokeRoleMutation,
    getComponentPermissions,
    updateComponentPermissions,
    setOrganization,
    joinTeam,
    leaveTeam,
  ]);

  // Clear cache on user change
  useEffect(() => {
    permissionCacheRef.current = new Map();
  }, [user?.id]);

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// Custom hook to use permissions
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionProvider");
  }
  return context;
};

// Helper hook for component permissions
export const useComponentPermissions = (componentId: string) => {
  const { getComponentPermissions } = usePermissions();
  return useMemo(
    () => getComponentPermissions(componentId),
    [getComponentPermissions, componentId],
  );
};

// Interface for batched permission checks
interface BatchedPermissionCheck extends PermissionCheck {
  id: string;
}

// Global request deduplication and batching
const pendingPermissionChecks = new Map<string, Promise<PermissionResult>>();
const batchQueue: Array<{ check: PermissionCheck; batchId: string; resolve: (result: PermissionResult) => void; reject: (error: Error) => void }> = [];
let batchTimeout: NodeJS.Timeout | null = null;

// Process batched permission checks
const processBatch = async () => {
  if (batchQueue.length === 0) return;

  const batch = [...batchQueue];
  batchQueue.length = 0; // Clear the queue

  try {
    // Group by user ID for bulk checking
    const bulkChecks: BatchedPermissionCheck[] = batch.map((item) => ({
      id: item.batchId,
      resource: item.check.resource,
      action: item.check.action,
      context: item.check.context,
    }));

    // Use bulk check API if available, otherwise process individually
    if (bulkChecks.length > 1) {
      try {
        const bulkResult = await permissionApi.checkBulkPermissions({
          checks: bulkChecks,
        });

        // Resolve each promise with its corresponding result
        batch.forEach((item) => {
          const resultKey = `${item.check.resource.type}-${item.check.resource.id}-${item.check.action}`;
          const result = bulkResult.results[item.batchId] ?? bulkResult.results[resultKey];
          if (result) {
            item.resolve(result);
          } else {
            // Fail closed when the bulk response does not include this check.
            item.resolve({
              allowed: false,
              evaluatedAt: new Date(),
              reason: 'Permission denied: bulk permission result missing for requested check'
            });
          }
        });
      } catch (bulkError) {
        // Fallback to individual checks
        await Promise.allSettled(
          batch.map(async (item) => {
            try {
              const result = await permissionApi.checkPermission(item.check);
              item.resolve(result);
            } catch (error) {
              console.warn('Individual permission check failed, denying access:', error);
              // Fail closed when the fallback individual check cannot be verified.
              item.resolve({
                allowed: false,
                evaluatedAt: new Date(),
                reason: 'Permission denied: individual permission check failed'
              });
            }
          })
        );
      }
    } else {
      // Single check
      const item = batch[0];
      try {
        const result = await permissionApi.checkPermission(item.check);
        item.resolve(result);
      } catch (error) {
        console.warn('Single permission check failed, denying access:', error);
        // Fail closed when the permission check cannot be verified.
        item.resolve({
          allowed: false,
          evaluatedAt: new Date(),
          reason: 'Permission denied: single permission check failed'
        });
      }
    }
  } catch (error) {
    // Fail closed in case of batch failure.
    console.warn('Batch processing failed, denying access to all items:', error);
    batch.forEach((item) => {
      item.resolve({
        allowed: false,
        evaluatedAt: new Date(),
        reason: 'Permission denied: batch permission processing failed'
      });
    });
  }
};

// Enhanced permission check with deduplication and batching
const checkPermissionWithDeduplication = async (
  check: PermissionCheck,
  cacheUserId?: string,
): Promise<PermissionResult> => {
  const authState = useAuthStore.getState();
  if (
    !authState.isAuthenticated ||
    !authState.tokens?.accessToken ||
    !authState.user?.id
  ) {
    return createUnauthenticatedPermissionResult();
  }

  const authenticatedUserId = cacheUserId || authState.user.id;
  const cacheKey = `${check.resource.type}:${check.resource.id}:${check.action}:${authenticatedUserId}`;

  // Check if request is already pending
  const existing = pendingPermissionChecks.get(cacheKey);
  if (existing) {
    return existing;
  }

  // Create new promise and add to pending requests
  const promise = new Promise<PermissionResult>((resolve, reject) => {
    // Generate unique batch ID
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

    // Add to batch queue
    batchQueue.push({ check, batchId, resolve, reject });

    // Set up batch processing timeout (shorter for responsiveness)
    if (batchTimeout) {
      clearTimeout(batchTimeout);
    }

    batchTimeout = setTimeout(() => {
      processBatch();
      batchTimeout = null;
    }, 50); // 50ms batch window
  });

  // Store the promise
  pendingPermissionChecks.set(cacheKey, promise);

  // Clean up after completion
  promise.finally(() => {
    pendingPermissionChecks.delete(cacheKey);
  });

  return promise;
};

// Helper hook for checking a specific permission with debouncing and caching
export const useHasPermission = (
  resource: { type: ResourceType; id: string },
  action: PermissionAction,
): boolean => {
  const { checkPermission } = usePermissions();
  const [hasPermission, setHasPermission] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const cacheRef = useRef<Map<string, { result: boolean; timestamp: number }>>(
    new Map(),
  );

  const cacheKey = `${resource.type}:${resource.id}:${action}`;
  const CACHE_DURATION = 30000; // 30 seconds cache
  const DEBOUNCE_DELAY = 150; // Reduced to 150ms for faster UI feedback

  useEffect(() => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Check cache first
    const cached = cacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setHasPermission(cached.result);
      return;
    }

    // Debounce the permission check
    timeoutRef.current = setTimeout(async () => {
      try {
        // Use enhanced permission check with deduplication
        const result = await checkPermission({ resource, action });
        const allowed = result.allowed;

        // Update cache with longer duration
        cacheRef.current.set(cacheKey, {
          result: allowed,
          timestamp: Date.now(),
        });

        setHasPermission(allowed);
      } catch (error) {
        console.warn("Permission check failed, defaulting to false:", error);
        setHasPermission(false);

        // Cache the failure for a shorter duration (5 seconds)
        cacheRef.current.set(cacheKey, {
          result: false,
          timestamp: Date.now() - CACHE_DURATION + 5000,
        });
      }
    }, DEBOUNCE_DELAY);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [checkPermission, resource.type, resource.id, action, cacheKey]);

  return hasPermission;
};
